/**
 * The gift-guide directory the signed-out footer renders — an Airbnb-style
 * column grid of guide links above the legal row.
 *
 * WHY A COMMITTED LIST. The footer ships inside the build-rendered homepage
 * body (src/prerender/renderRoute.tsx), the one page a crawler reads without
 * executing JavaScript — so these links are the crawl graph's entry points
 * into the /guide/* surface and must exist at build time. The live Firestore
 * index (usePublishedGuides) can't serve that: effects never run in a static
 * render. A hand-curated grouping also beats deriving from browse order,
 * which is Kate's merchandising order and reshuffles weekly; the footer
 * should hold stable, high-search-intent evergreen guides under headings a
 * search engine (and a human) can place.
 *
 * KEPT HONEST BY src/theaWeb/seo/__tests__/footerGuideLinks.test.ts: every
 * slug must exist in scripts/data/published-guides.json with this exact
 * title (the footer and the page it lands on must agree) and must be an
 * evergreen listing (a season-gated guide would be a dead-feeling link for
 * most of the year). Unpublishing a linked guide fails the build instead of
 * shipping a footer link into a 404.
 *
 * Hrefs are the canonical trailing-slash form (scripts/og/site.js): a crawler
 * following them skips Hosting's /guide/<slug> → /guide/<slug>/ 301 hop.
 */

export interface FooterGuideLink {
  /** Published guide slug — the /guide/<slug>/ path segment. */
  slug: string;
  /** Mirrors the guide's own title in the published snapshot. */
  title: string;
}

export interface FooterGuideSection {
  heading: string;
  links: FooterGuideLink[];
}

export const FOOTER_GUIDE_SECTIONS: FooterGuideSection[] = [
  {
    heading: 'Occasions',
    links: [
      { slug: 'birthday', title: 'Birthday' },
      { slug: 'anniversary', title: 'Anniversary' },
      { slug: 'wedding', title: 'Wedding' },
      { slug: 'housewarming', title: 'Housewarming' },
      { slug: 'graduation', title: 'Graduation' },
      { slug: 'new-baby', title: 'New baby' },
      { slug: 'just-engaged', title: 'Newly engaged' },
      { slug: 'thank-you', title: 'Thank you' },
      { slug: 'teacher-appreciation', title: 'Teacher appreciation' },
    ],
  },
  {
    heading: 'For her',
    links: [
      { slug: 'bookworm-mom', title: 'Book lover' },
      { slug: 'green-thumb-mama', title: 'Green thumb' },
      { slug: 'crafty-mama', title: 'Craft lover' },
      { slug: 'workout-mama', title: 'Fitness lover' },
      { slug: 'tea-drinker-mama', title: 'Tea lover' },
      { slug: 'sentimental-grandma', title: 'Sentimental grandma' },
      { slug: 'fine-jewelry', title: 'Fine jewelry' },
      { slug: 'personalized-jewelry', title: 'Personalized jewelry' },
      { slug: 'cult-favorite-beauty', title: 'Cult-Favorite Beauty' },
    ],
  },
  {
    heading: 'For him',
    links: [
      { slug: 'chef-dad', title: 'Home chef' },
      { slug: 'golfer-dad', title: 'Golfer' },
      { slug: 'fishing', title: 'Fisherman' },
      { slug: 'morning-joe-dad', title: 'Coffee lover' },
      { slug: 'mountain-man-dad', title: 'Mountain man' },
      { slug: 'whiskey-lover', title: 'Whiskey Lover' },
      { slug: 'beard-and-shave', title: 'Beard & shave' },
      { slug: 'mens-jewelry', title: 'Jewelry for him' },
      { slug: 'personal-dad-gifts', title: 'Personalized for him' },
    ],
  },
  {
    heading: 'For kids',
    links: [
      { slug: 'dinosaur-kid', title: 'Dinosaur kid' },
      { slug: 'little-artist', title: 'Little artist' },
      { slug: 'little-scientist', title: 'Little scientist' },
      { slug: 'little-chef', title: 'Little chef' },
      { slug: 'boys-who-hoop', title: 'Basketball boy' },
      { slug: 'girls-who-hoop', title: 'Basketball girl' },
      { slug: 'little-ballerina', title: 'Little ballerina' },
      { slug: 'girls-dolls', title: 'Little doll lover' },
      { slug: 'baseball-kid', title: 'Baseball kid' },
    ],
  },
  {
    heading: 'Food & drink',
    links: [
      { slug: 'coffee-tea-matcha', title: 'Coffee, tea & matcha' },
      { slug: 'home-mixologist', title: 'Home mixologist' },
      { slug: 'wine-lover', title: 'Wine Lover' },
      { slug: 'gin-lover', title: 'Gin lover' },
      { slug: 'tequila-lover', title: 'Tequila lover' },
      { slug: 'fancy-pantry', title: 'Fancy pantry' },
      { slug: 'sweets-from-around-the-world', title: 'Sweets from around the world' },
      { slug: 'spicy-food-lover', title: 'Spicy Food Lover' },
      { slug: 'zero-proof', title: 'Zero-proof' },
    ],
  },
  {
    heading: 'Books & more',
    links: [
      { slug: 'bestselling-fiction', title: 'Bestselling fiction' },
      { slug: 'bestselling-thrillers', title: 'Bestselling thrillers & mysteries' },
      { slug: 'bestselling-fantasy-scifi', title: 'Bestselling fantasy & sci-fi' },
      { slug: 'bestselling-cookbooks', title: 'Bestselling cookbooks' },
      { slug: 'hit-books-2026', title: 'Hit books of 2026' },
      { slug: 'last-minute-gifts', title: 'Last-minute gifts' },
      { slug: 'hostess-gifts-under-30', title: 'Hostess gifts under $30' },
      { slug: 'date-night', title: 'Date night in' },
      { slug: 'family-game-night', title: 'Family game night' },
    ],
  },
];

/** Flat view for tests and anywhere the grouping doesn't matter. */
export const FOOTER_GUIDE_LINKS: FooterGuideLink[] = FOOTER_GUIDE_SECTIONS.flatMap(
  (section) => section.links,
);

export function footerGuideHref(slug: string): string {
  return `/guide/${slug}/`;
}
